<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Devenir <strong>photographe de visite virtuelle</strong> est l'une
            des carrières créatives les plus prometteuses de 2026. La demande
            explose dans l'immobilier, l'hôtellerie, la restauration et les
            institutions culturelles. Et la bonne nouvelle : vous pouvez
            démarrer avec votre smartphone et quelques centaines d'euros de
            matériel.
          </p>
        </section>

        <section>
          <h2>Qu'est-ce qu'un photographe de visite virtuelle ?</h2>
          <p>
            Un photographe de visite virtuelle (ou "virtual tour videographer"
            en anglais) est un professionnel qui capture, assemble et livre des
            visites virtuelles 360° interactives pour ses clients. Il se
            distingue d'un photographe immobilier classique par sa maîtrise des
            panoramas sphériques, des logiciels de visite virtuelle et des
            hotspots interactifs.
          </p>
          <p>
            Les clients types incluent : les agences immobilières, les hôtels,
            les restaurants, les musées, les showrooms, les cliniques et les
            établissements scolaires.
          </p>
        </section>

        <section>
          <h2>Compétences requises</h2>
          <h3>Compétences techniques</h3>
          <ul>
            <li>
              Maîtrise d'une application de visite virtuelle (Travvir, Pano2VR,
              Matterport)
            </li>
            <li>
              Compréhension des bases de la photographie (exposition, blanc,
              profondeur de champ)
            </li>
            <li>
              Capacité à travailler avec différentes conditions de lumière
            </li>
            <li>
              Notions de base en édition photo (correction de l'exposition,
              retouche des raccords)
            </li>
          </ul>
          <h3>Compétences commerciales</h3>
          <ul>
            <li>Prospection et gestion de la relation client</li>
            <li>
              Établissement de devis et facturation (logiciel de gestion
              micro-entreprise)
            </li>
            <li>Communication professionnelle et réactivité</li>
            <li>
              Présence sur les réseaux sociaux professionnels (LinkedIn,
              Instagram)
            </li>
          </ul>
        </section>

        <section>
          <h2>Matériel de départ recommandé</h2>
          <table>
            <thead>
              <tr>
                <th>Équipement</th>
                <th>Option budget</th>
                <th>Option pro</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Caméra principale</td>
                <td>Smartphone récent (0 €)</td>
                <td>Insta360 X4 (450 €)</td>
              </tr>
              <tr>
                <td>Trépied</td>
                <td>Gorillapod flexible (30 €)</td>
                <td>Trépied carbone léger (150 €)</td>
              </tr>
              <tr>
                <td>Application</td>
                <td>Travvir essai gratuit</td>
                <td>Travvir Pro (tarif par palier propriété)</td>
              </tr>
              <tr>
                <td>Éclairage d'appoint</td>
                <td>Panneau LED portatif (40 €)</td>
                <td>Kit 3 panneaux LED (200 €)</td>
              </tr>
              <tr>
                <td>Stockage</td>
                <td>Clé USB 256 Go (20 €)</td>
                <td>SSD externe 1 To (80 €)</td>
              </tr>
              <tr>
                <td><strong>Total</strong></td>
                <td><strong>~90 €</strong></td>
                <td><strong>~900 €</strong></td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Comment trouver ses premiers clients</h2>

          <h3>Phase 1 : Construire un portfolio (semaines 1–4)</h3>
          <p>
            Créez 3 à 5 visites virtuelles gratuitement pour des proches (leur
            logement, leur commerce) ou proposez votre service gratuitement à
            une agence immobilière locale en échange d'un témoignage. Ces
            exemples constituent votre portfolio de départ.
          </p>

          <h3>Phase 2 : Prospection directe (mois 2–3)</h3>
          <p>
            Identifiez les agences immobilières, restaurants et hôtels de votre
            zone géographique dont la fiche Google Business n'a pas de visite
            virtuelle. Envoyez un e-mail personnalisé avec un exemple de visite
            et votre tarif d'entrée de gamme (100–150 € pour commencer).
          </p>

          <h3>Phase 3 : Développement par recommandation (mois 4+)</h3>
          <p>
            Un client satisfait recommande en moyenne 2,3 nouveaux clients.
            Demandez systématiquement des recommandations et des avis Google.
            Augmentez progressivement vos tarifs à mesure que votre réputation
            se construit.
          </p>
        </section>

        <section>
          <h2>Revenus potentiels</h2>
          <table>
            <thead>
              <tr>
                <th>Niveau d'activité</th>
                <th>Visites/semaine</th>
                <th>Tarif moyen</th>
                <th>CA mensuel</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Débutant (temps partiel)</td>
                <td>2</td>
                <td>150 €</td>
                <td>~1 200 €</td>
              </tr>
              <tr>
                <td>Intermédiaire (semi-pro)</td>
                <td>4</td>
                <td>250 €</td>
                <td>~4 000 €</td>
              </tr>
              <tr>
                <td>Expert (plein temps)</td>
                <td>6</td>
                <td>400 €</td>
                <td>~9 600 €</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>
            Faut-il une formation officielle pour devenir photographe de visite
            virtuelle ?
          </h3>
          <p>
            Non. Il n'existe pas de certification officielle en France pour ce
            métier. Votre portfolio et vos références clients valent bien plus
            qu'un diplôme. Des formations en ligne (Udemy, YouTube) et la
            pratique régulière suffisent pour se lancer.
          </p>

          <h3>Est-ce un marché saturé en France ?</h3>
          <p>
            Non. Malgré la croissance rapide de la demande, la majorité des PME
            françaises ne dispose toujours pas de visite virtuelle. Les zones
            géographiques en dehors de Paris, Lyon et Bordeaux sont
            particulièrement sous-desservies et représentent une opportunité
            significative.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/prestation-visite-virtuelle">Prestation Visite Virtuelle : Comment Vendre ses Services en 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-tarif-prix">Visite Virtuelle : Tarifs et Prix en 2026 — Guide Complet</a></li>
          <li><a href="/blog/fr/photographie-interieure-360-visite-virtuelle">Photographie d'Intérieur pour Visites Virtuelles 360° : Guide Technique</a></li>
          <li><a href="/blog/fr/guide-photographie-360-immobilier">Guide Complet Photographie 360° Immobilier 2026</a></li>
          <li><a href="/blog/fr/meilleur-logiciel-visite-virtuelle-immobilier">Meilleur Logiciel de Visite Virtuelle Immobilier 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "photographe-visite-virtuelle-devenir",
);

useBlogSeo({
  title: "Comment Devenir Photographe de Visite Virtuelle en 2026 | Travvir",
  description:
    "Guide complet pour devenir photographe de visite virtuelle : compétences, matériel, trouver des clients, tarifs et revenus potentiels. Lancez votre activité en France avec moins de 100 €.",
  keywords:
    "photographe visite virtuelle, devenir photographe visite 360, métier visite virtuelle, freelance visite virtuelle, formation photographe visite virtuelle",
  slug: "photographe-visite-virtuelle-devenir",
  image: postData.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question:
        "Faut-il une formation officielle pour devenir photographe de visite virtuelle ?",
      answer:
        "Non. Il n'existe pas de certification officielle en France. Votre portfolio et vos références valent plus qu'un diplôme.",
    },
    {
      question: "Est-ce un marché saturé en France ?",
      answer:
        "Non. La majorité des PME françaises ne dispose toujours pas de visite virtuelle. Les zones hors Paris/Lyon/Bordeaux sont particulièrement sous-desservies.",
    },
  ],
});

</script>
